import { useState, useEffect, useRef } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Shield, Award, Heart, Star, Calendar } from "lucide-react";
import KennelSEO, { localBusinessSchema, organizationSchema, animalBreederSchema, reviewSchema, breadcrumbSchema } from "./KennelSEO";
import MagneticButton from "../../components/MagneticButton";

const BASE = "https://wolfevomkatzenberg.pl";

const useParallax = (speed = 0.3) => {
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const windowH = window.innerHeight;
      if (rect.top < windowH && rect.bottom > 0) {
        setOffset((rect.top - windowH / 2) * speed);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [speed]);
  return { ref, offset };
};

const TypeWriter = ({ text, delay = 0, speed = 50 }) => {
  const [displayed, setDisplayed] = useState("");
  const [started, setStarted] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setStarted(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);
  useEffect(() => {
    if (!started) return;
    let i = 0;
    const interval = setInterval(() => {
      if (i < text.length) { setDisplayed(text.slice(0, i + 1)); i++; }
      else { setDone(true); clearInterval(interval); }
    }, speed);
    return () => clearInterval(interval);
  }, [started, text, speed]);
  return <>{displayed}{!done && <span className="animate-pulse">|</span>}</>;
};

const AnimatedCounter = ({ target, suffix = "", duration = 2 }) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started) setStarted(true);
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);
  useEffect(() => {
    if (!started) return;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(current));
    }, (duration * 1000) / steps);
    return () => clearInterval(timer);
  }, [started, target, duration]);
  return <span ref={ref}>{count}{suffix}</span>;
};

const KennelHome = () => {
  const { settings, API } = useOutletContext();
  const [dogs, setDogs] = useState([]);
  const [news, setNews] = useState([]);
  const [plans, setPlans] = useState([]);
  const [reviews, setReviews] = useState([]);
  const aboutParallax = useParallax(0.15);
  const breedParallax = useParallax(0.15);

  useEffect(() => {
    fetch(`${API}/dogs`).then(r => r.json()).then(setDogs);
    fetch(`${API}/news`).then(r => r.json()).then(d => setNews(d.slice(0, 3)));
    fetch(`${API}/breeding-plans`).then(r => r.json()).then(setPlans);
    fetch(`${API}/reviews`).then(r => r.json()).then(d => setReviews(Array.isArray(d) ? d : [])).catch(() => {});
  }, [API]);

  return (
    <div>
      <KennelSEO
        title="Wolfe vom Katzenberg — Hodowla Owczarka Niemieckiego | ZKwP FCI"
        description="Profesjonalna hodowla owczarka niemieckiego Wolfe vom Katzenberg. Szczenięta z rodowodem FCI, badania HD/ED/DNA, socjalizacja. Poznaj nasze psy hodowlane i aktualne mioty."
        canonical={`${BASE}/katzenberg`}
        ogImage={`${BASE}/logo_katzenberg_new.png`}
        schema={[localBusinessSchema, organizationSchema, animalBreederSchema]}
      />
      {/* ═══ HERO ═══ */}
      <section className="relative min-h-[100vh] flex items-center justify-center overflow-hidden bg-[#0B0B0D]">
        <div className="absolute inset-0 bg-black/50 z-10" />
        <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover opacity-60">
          <source src="/katzenberg_hero.mp4" type="video/mp4" />
        </video>
        {/* Fallback dark overlay for when video doesn't load */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0D]/30 via-transparent to-[#0B0B0D] z-10" />

        <div className="relative z-20 text-center px-6 max-w-4xl">
          <motion.img src="/logo_katzenberg_new.png" alt="" initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1 }}
            className="h-28 md:h-40 w-auto mx-auto mb-10 drop-shadow-2xl"
          />
          <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3, delay: 0.4 }}
            className="font-serif text-[clamp(3rem,8vw,6rem)] text-[#F5F5F5] leading-[0.95] tracking-tight"
          >
            <span className="block"><TypeWriter text="WOLFE VOM" delay={600} speed={120} /></span>
            <span className="block text-[#D4AF37]"><TypeWriter text="KATZENBERG" delay={1800} speed={120} /></span>
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 3.2 }}
            className="text-[#B8B8B8] text-sm md:text-base mt-6 mb-2 tracking-wide"
          >Hodowla Owczarka Niemieckiego</motion.p>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 3.6 }}
            className="text-[#C8A96A] text-xs tracking-[0.3em] mb-12"
          >PASJA · CHARAKTER · DOSKONAŁOŚĆ</motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 4 }} className="flex flex-wrap gap-4 justify-center">
            <MagneticButton strength={0.4}>
              <Link to="/psy" data-testid="hero-cta-dogs"
                className="block px-10 py-3.5 bg-[#D4AF37] text-[#0B0B0D] text-xs font-bold tracking-[0.2em] hover:bg-[#C8A96A] transition-all duration-300 hover:shadow-lg hover:shadow-[#D4AF37]/20"
              >NASZE PSY</Link>
            </MagneticButton>
            <MagneticButton strength={0.4}>
              <Link to="/szczenieta" data-testid="hero-cta-puppies"
                className="block px-10 py-3.5 border border-[#D4AF37] text-[#D4AF37] text-xs tracking-[0.2em] hover:bg-[#D4AF37] hover:text-[#0B0B0D] transition-all duration-300"
              >SZCZENIĘTA</Link>
            </MagneticButton>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20"
        >
          <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 2, repeat: Infinity }} className="w-px h-12 bg-gradient-to-b from-[#D4AF37]/60 to-transparent" />
        </motion.div>
      </section>

      {/* ═══ STATS ═══ */}
      <section className="bg-[#141416] border-y border-[#2A2A2D]">
        <div className="max-w-6xl mx-auto grid grid-cols-3 divide-x divide-[#2A2A2D]">
          {[
            { icon: Shield, label: "Pełne badania", sub: "HD · ED · DNA" },
            { icon: Award, label: "Wystawy & sport", sub: "Tytuły championów" },
            { icon: Heart, label: "Hodowla rodzinna", sub: "Pasja i doświadczenie" },
          ].map(({ icon: Icon, label, sub }) => (
            <div key={label} className="flex items-center justify-center gap-3 py-7 px-4">
              <Icon className="w-5 h-5 text-[#D4AF37]" />
              <div>
                <p className="text-xs text-[#F5F5F5] font-medium">{label}</p>
                <p className="text-[10px] text-[#B8B8B8]/60">{sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ O HODOWLI ═══ */}
      <section className="bg-[#0B0B0D]">
        <div className="grid md:grid-cols-2 max-w-7xl mx-auto">
          <motion.div ref={aboutParallax.ref} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 1 }}
            className="relative overflow-hidden min-h-[450px] gallery-wow" style={{ cursor: 'default' }}
          >
            <img src="/about_puppy.jpg" alt="Owczarek niemiecki" className="w-full h-full object-cover scale-110" style={{ transform: `translateY(${aboutParallax.offset}px) scale(1.1)` }} />
            <div className="wow-overlay" />
          </motion.div>
          <div className="flex items-center px-8 md:px-16 py-16">
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-4">O HODOWLI</span>
              <h2 className="font-serif text-[clamp(1.6rem,3vw,2.8rem)] text-[#F5F5F5] mb-6 leading-tight">
                Z miłości do<br />owczarków niemieckich
              </h2>
              <div className="w-12 h-px bg-[#D4AF37]/40 mb-6" />
              <p className="text-[#B8B8B8] text-sm leading-relaxed mb-8 max-w-md">
                {settings?.about || "Nasza hodowla to pasja i wieloletnie doświadczenie. Każdy pies jest członkiem naszej rodziny. Stawiamy na zdrowie, charakter i piękno zgodne ze standardem rasy FCI."}
              </p>
              <MagneticButton strength={0.35}>
                <Link to="/o-hodowli" data-testid="about-cta"
                  className="px-8 py-3 border border-[#D4AF37] text-[#D4AF37] text-xs tracking-[0.15em] hover:bg-[#D4AF37] hover:text-[#0B0B0D] transition-all duration-300 inline-block"
                >POZNAJ NAS</Link>
              </MagneticButton>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ NASZE PSY ═══ */}
      {dogs.length > 0 && (
        <section className="bg-[#141416] px-6 md:px-12 py-24">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-14">
              <div>
                <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-3">HODOWLA</span>
                <h2 className="font-serif text-[clamp(1.8rem,3.5vw,3rem)] text-[#F5F5F5]">Nasze psy</h2>
                <div className="w-12 h-px bg-[#D4AF37]/30 mt-5" />
              </div>
              <Link to="/psy" data-testid="see-all-dogs"
                className="text-xs tracking-wider text-[#B8B8B8]/40 hover:text-[#D4AF37] transition-colors flex items-center gap-2 group"
              >Zobacz wszystkie <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {dogs.slice(0, 3).map((dog, idx) => (
                <Link key={dog.id} to={`/psy/${dog.id}`} data-testid={`home-dog-${dog.id}`}>
                  <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                    transition={{ delay: idx * 0.15, duration: 0.5 }}
                  >
                    <div className="bg-[#1A1A1D] gallery-wow aspect-[3/4]">
                      {dog.image_url ? (
                        <img src={`${dog.image_url}?v=2`} alt={dog.name} className="w-full h-full object-cover object-top" />
                      ) : (
                        <div className="w-full aspect-[3/4] flex items-center justify-center text-[#B8B8B8]/10 text-6xl font-serif">{dog.name[0]}</div>
                      )}
                      <div className="wow-overlay" />
                      <div className="wow-label">
                        <p className="text-[10px] tracking-[0.2em] text-[#D4AF37]/60 mb-1 uppercase">{dog.titles?.split(",")[0]?.trim()}</p>
                        {dog.registered_name && <p className="text-[11px] text-[#B8B8B8]/60 font-light tracking-wide mb-2">{dog.registered_name}</p>}
                        {dog.short_description && <p className="text-[12px] text-[#B8B8B8]/60 leading-relaxed mb-2">{dog.short_description}</p>}
                        <span className="wow-label-text">ZOBACZ PROFIL</span>
                      </div>
                      {dog.titles && (
                        <div className="absolute top-4 left-4 z-[7]">
                          <span className="inline-flex items-center gap-1 text-[8px] px-2 py-1 bg-[#0B0B0D]/50 backdrop-blur-sm text-[#B8B8B8]/40 border border-[#B8B8B8]/10 tracking-wider">
                            <Star className="w-2.5 h-2.5" /> {dog.titles.split(",")[0].trim()}
                          </span>
                        </div>
                      )}
                    </div>
                  </motion.div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ DLACZEGO OWCZAREK NIEMIECKI ═══ */}
      <section className="bg-[#0B0B0D] px-6 md:px-12 py-24 relative overflow-hidden">
        <div className="absolute bottom-0 left-0 w-[500px] h-[300px] bg-[#D4AF37]/[0.03] rounded-full blur-[100px] translate-y-1/2 -translate-x-1/4 pointer-events-none" />
        <div className="max-w-5xl mx-auto relative">
          <div className="text-center mb-14">
            <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-4">RASA</span>
            <h2 className="font-serif text-[clamp(1.6rem,3vw,2.8rem)] text-[#F5F5F5] mb-6 leading-tight">Dlaczego owczarek niemiecki?</h2>
            <div className="w-12 h-px bg-[#D4AF37]/40 mx-auto" />
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <p className="text-[#B8B8B8] text-sm leading-relaxed mb-8">
                Owczarek niemiecki to jedna z najbardziej wszechstronnych ras psów na świecie. Ceniony jest za inteligencję, lojalność oraz wyjątkową zdolność do pracy z człowiekiem. Psy tej rasy doskonale sprawdzają się zarówno jako towarzysze rodziny, jak i w sporcie czy pracy użytkowej. Odpowiedzialna hodowla dba o zdrowie, charakter i stabilną psychikę kolejnych pokoleń tej wyjątkowej rasy.
              </p>
              <MagneticButton strength={0.35}>
                <Link to="/owczarek-niemiecki" data-testid="breed-snippet-cta"
                  className="px-8 py-3 border border-[#D4AF37] text-[#D4AF37] text-xs tracking-[0.15em] hover:bg-[#D4AF37] hover:text-[#0B0B0D] transition-all duration-300 inline-flex items-center gap-2"
                >DOWIEDZ SIĘ WIĘCEJ O RASIE <ArrowRight className="w-3.5 h-3.5" /></Link>
              </MagneticButton>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div ref={breedParallax.ref} className="overflow-hidden bg-[#1A1A1D] border border-[#2A2A2D] rounded max-h-[400px] gallery-wow" style={{ cursor: 'default' }}>
                <img src="/breed_puppies.jpg" alt="Szczenięta owczarka niemieckiego" className="w-full h-full object-cover object-center scale-110" style={{ transform: `translateY(${breedParallax.offset}px) scale(1.1)` }} />
                <div className="wow-overlay" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ NADCHODZĄCE MIOTY ═══ */}
      {plans.length > 0 && (
        <section className="bg-[#0B0B0D] px-6 md:px-12 py-24 relative overflow-hidden">
          {/* Background glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] bg-[#D4AF37]/[0.04] rounded-full blur-[120px] pointer-events-none" />
          <div className="max-w-7xl mx-auto relative">
            <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-3">PLANY HODOWLANE</span>
            <h2 className="font-serif text-[clamp(1.8rem,3.5vw,3rem)] text-[#F5F5F5] mb-4">Nadchodzące mioty</h2>
            <div className="w-12 h-px bg-[#D4AF37]/30 mb-14" />
            <div className="space-y-8">
              {plans.map((plan, idx) => (
                <motion.div key={plan.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="relative bg-[#1A1A1D] border border-[#D4AF37]/15 hover:border-[#D4AF37]/40 transition-all duration-500 overflow-hidden group"
                >
                  {/* Card glow effect */}
                  <div className="absolute -top-20 -right-20 w-60 h-60 bg-[#D4AF37]/[0.06] rounded-full blur-[80px] group-hover:bg-[#D4AF37]/[0.12] transition-all duration-700 pointer-events-none" />
                  <div className="absolute -bottom-16 -left-16 w-40 h-40 bg-[#D4AF37]/[0.04] rounded-full blur-[60px] pointer-events-none" />
                  <div className="grid md:grid-cols-[420px_1fr] relative">
                    <div className="aspect-video md:aspect-auto overflow-hidden bg-[#141416] min-h-[280px] relative gallery-wow" style={{ transform: 'none' }}>
                      {plan.image_url ? (
                        <img src={plan.image_url} alt={plan.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center p-8 relative">
                          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "repeating-linear-gradient(45deg, #D4AF37 0px, #D4AF37 1px, transparent 1px, transparent 30px)" }} />
                          <div className="w-20 h-20 rounded-full border border-[#D4AF37]/20 flex items-center justify-center mb-4 group-hover:border-[#D4AF37]/40 transition-colors">
                            <Heart className="w-8 h-8 text-[#D4AF37]/30 group-hover:text-[#D4AF37]/60 transition-colors" />
                          </div>
                          <p className="text-[10px] text-[#D4AF37]/30 tracking-[0.2em] group-hover:text-[#D4AF37]/50 transition-colors">PLANOWANY MIOT</p>
                        </div>
                      )}
                    </div>
                    <div className="p-8 md:p-10 flex flex-col justify-center relative">
                      {plan.expected_date && (
                        <span className="text-[10px] tracking-[0.15em] text-[#D4AF37] border border-[#D4AF37]/25 bg-[#D4AF37]/[0.05] px-4 py-1.5 self-start mb-5 flex items-center gap-1.5 group-hover:bg-[#D4AF37]/[0.1] transition-colors">
                          <Calendar className="w-3 h-3" /> {plan.expected_date}
                        </span>
                      )}
                      <h3 className="font-serif text-2xl text-[#F5F5F5] group-hover:text-[#D4AF37] transition-colors mb-3">{plan.title}</h3>
                      <p className="text-[#B8B8B8]/60 text-sm mb-4 flex items-center gap-2">
                        <Heart className="w-4 h-4 text-[#D4AF37]/40" /> {plan.mother_name} × {plan.father_name}
                      </p>
                      {plan.description && <p className="text-[#B8B8B8]/40 text-sm leading-relaxed mb-6">{plan.description}</p>}
                      <MagneticButton strength={0.35}>
                        <Link to="/kontakt"
                          className="text-xs tracking-[0.15em] text-[#D4AF37] border border-[#D4AF37]/20 px-6 py-2.5 self-start hover:bg-[#D4AF37] hover:text-[#0B0B0D] transition-all duration-300 flex items-center gap-2 group/link"
                        >ZAPYTAJ O SZCZENIĘTA <ArrowRight className="w-3.5 h-3.5 group-hover/link:translate-x-1 transition-transform" /></Link>
                      </MagneticButton>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ OPINIE ═══ */}
      <section className="bg-[#141416] px-6 md:px-12 py-24">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-3">OPINIE</span>
            <h2 className="font-serif text-[clamp(1.8rem,3.5vw,3rem)] text-[#F5F5F5] mb-6">Opinie właścicieli naszych psów</h2>
            <div className="w-12 h-px bg-[#D4AF37]/30 mx-auto mb-6" />
            <p className="text-[#B8B8B8]/60 text-sm leading-relaxed max-w-2xl mx-auto">
              Największą satysfakcją dla hodowcy jest kontakt z właścicielami naszych psów i obserwowanie, jak rozwijają się w nowych domach.
            </p>
          </div>
          
          {/* Reviews from API */}
          {reviews.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#B8B8B8]/30 text-sm italic">Wkrótce pojawią się tu opinie naszych klientów</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="home-reviews-grid">
              {reviews.slice(0, 6).map((review, i) => (
                <motion.div
                  key={review.id || i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="bg-[#0B0B0D] border border-[#2A2A2D] p-6 rounded-lg hover:border-[#D4AF37]/30 transition-colors"
                  data-testid={`home-review-card-${i}`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    {review.image_url ? (
                      <img src={review.image_url} alt={review.dog_name} className="w-12 h-12 rounded-full object-cover border border-[#2A2A2D]" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                        <Heart className="w-5 h-5 text-[#D4AF37]/60" />
                      </div>
                    )}
                    <div>
                      <p className="text-[#F5F5F5] text-sm font-medium">{review.dog_name}</p>
                      <p className="text-[#B8B8B8]/50 text-xs">
                        {review.owner_name}{review.owner_city ? ` • ${review.owner_city}` : ""}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1 mb-3">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} className={`w-3.5 h-3.5 ${s < (review.rating || 5) ? "text-[#D4AF37] fill-[#D4AF37]" : "text-[#2A2A2D]"}`} />
                    ))}
                  </div>
                  <p className="text-[#B8B8B8] text-sm leading-relaxed italic">{review.text}</p>
                  {review.breeder_response && (
                    <div className="mt-4 pl-3 border-l-2 border-[#D4AF37]/30">
                      <p className="text-[#D4AF37] text-xs font-medium mb-1">Odpowiedź hodowli:</p>
                      <p className="text-[#B8B8B8]/70 text-xs italic">{review.breeder_response}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ═══ CTA KONTAKT ═══ */}
      <section className="bg-[#0B0B0D] px-6 md:px-12 py-28 border-t border-[#2A2A2D]">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-6">KONTAKT</span>
            <h2 className="font-serif text-[clamp(1.5rem,3vw,2.5rem)] text-[#F5F5F5] mb-6">Zainteresowany naszymi psami?</h2>
            <div className="w-12 h-px bg-[#D4AF37]/30 mx-auto mb-8" />
            <p className="text-[#B8B8B8]/50 text-sm mb-10">Skontaktuj się z nami — chętnie odpowiemy na pytania dotyczące hodowli, planowanych miotów i dostępnych szczeniąt.</p>
            <MagneticButton strength={0.4}>
              <Link to="/kontakt" data-testid="bottom-cta"
                className="px-12 py-4 bg-[#D4AF37] text-[#0B0B0D] text-xs font-bold tracking-[0.2em] hover:bg-[#C8A96A] transition-all duration-300 inline-block hover:shadow-lg hover:shadow-[#D4AF37]/20"
              >NAPISZ DO NAS</Link>
            </MagneticButton>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default KennelHome;
